import {
  Component,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import type { ApprovalView, FeedbackView, StatusView } from '@alfred/shared';
import { api, ApiFailure, describeError } from './api.js';
import { ActivityView } from './ActivityView.js';
import { Approvals } from './Approvals.js';
import { isPostDraft, PostsView } from './PostsView.js';
import { CostsView } from './CostsView.js';
import { BudgetView } from './BudgetView.js';
import { Feedback } from './Feedback.js';
import { Orb } from './Orb.js';
import { Rich } from './Rich.js';
import { Settings } from './Settings.js';
import { VoiceSession, type VoiceState } from './voice.js';

// three.js is big: load the HUD only when it's about to be shown.
const Hud = lazy(() => import('./Hud.js'));

interface Message {
  id: string;
  role: 'you' | 'alfred' | 'error';
  text: string;
  sessionId: string | null;
  meta?: string;
  /** Alfred's answers: the turn id feedback attaches to. */
  turnId?: string;
  feedback?: FeedbackView | null;
  /** What Alfred remembered from this exchange. */
  remembered?: string[];
}

const SESSION_KEY = 'alfred.sessionId';
const VIEWS = ['chat', 'posts', 'activity', 'costs', 'budget'] as const;
type View = (typeof VIEWS)[number];
const VIEW_LABELS: Record<View, string> = {
  chat: 'Chat',
  posts: 'Posts',
  activity: 'Activity',
  costs: 'Costs',
  budget: 'Budget',
};

/** The current tab lives in the URL hash so Back works and links can open a tab. */
function viewFromHash(): View {
  const v = window.location.hash.replace(/^#\/?/, '');
  return (VIEWS as readonly string[]).includes(v) ? (v as View) : 'chat';
}
const POLL_MS = 30_000;
/** Memories are saved in the background after a reply: look for them a little later. */
const REMEMBERED_CHECKS_MS = [4_000, 12_000];

const VOICE_LABELS: Record<VoiceState, string> = {
  idle: 'Talk to Alfred',
  listening: 'Listening… tap to stop',
  transcribing: 'Hearing you… tap to stop',
  thinking: 'Thinking… tap to stop',
  speaking: 'Speaking… tap to interrupt',
};

/**
 * A decision check-in notification opens "/?checkin=<id>": start with the message ready to send.
 */
function checkInDraft(): string {
  const id = new URLSearchParams(window.location.search).get('checkin');
  return id && /^[0-9a-f]{8}$/.test(id) ? `Let's do my check-in on decision ${id}.` : '';
}

function storedSession(): string | null {
  try {
    return localStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}
function storeSession(id: string | null) {
  try {
    if (id) localStorage.setItem(SESSION_KEY, id);
    else localStorage.removeItem(SESSION_KEY);
  } catch {
    // Private mode etc.: conversation still continues for this page's lifetime.
  }
}

export function Main({ onSignedOut }: { onSignedOut: () => void }) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(storedSession);
  const [draft, setDraft] = useState(checkInDraft);
  const [thinking, setThinking] = useState(false);
  const [approvals, setApprovals] = useState<ApprovalView[]>([]);
  // Things to post live in the Posts tab; everything else waits in the chat.
  const postDrafts = approvals.filter(isPostDraft);
  const otherApprovals = approvals.filter((a) => !isPostDraft(a));
  const [status, setStatus] = useState<StatusView | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [view, setView] = useState<View>(viewFromHash);

  // Tidy "?checkin=" out of the address so a reload doesn't fill the draft again.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('checkin')) return;
    params.delete('checkin');
    const rest = params.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`,
    );
  }, []);

  useEffect(() => {
    const onHash = () => setView(viewFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  const go = (v: View) => {
    window.location.hash = v === 'chat' ? '' : `/${v}`;
    setView(v);
  };
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const refreshSide = useCallback(async () => {
    const [a, s] = await Promise.allSettled([api.approvals(), api.status()]);
    if (a.status === 'fulfilled') setApprovals(a.value);
    if (s.status === 'fulfilled') setStatus(s.value);
  }, []);

  // Load history once; continue the most recent conversation (from any device).
  useEffect(() => {
    void api
      .history()
      .then((turns) => {
        setMessages(
          turns.flatMap((t, i) => [
            // Briefings are Alfred's own idea: show only his answer.
            ...(t.channel === 'briefing' ||
            t.channel === 'scheduled' ||
            t.channel === 'content' ||
            t.channel === 'review'
              ? []
              : [{ id: `h${i}u`, role: 'you' as const, text: t.prompt, sessionId: t.sessionId }]),
            {
              id: `h${i}a`,
              role: 'alfred' as const,
              text: t.text,
              sessionId: t.sessionId,
              turnId: t.id,
              feedback: t.feedback,
              ...(t.remembered ? { remembered: t.remembered } : {}),
              ...(t.channel === 'voice' ? { meta: 'by phone' } : {}),
              ...(t.channel === 'briefing' ? { meta: 'Morning briefing' } : {}),
              ...(t.channel === 'content' ? { meta: 'Content report' } : {}),
              ...(t.channel === 'review' ? { meta: 'Weekly review' } : {}),
              ...(t.channel === 'scheduled'
                ? { meta: t.prompt.match(/^Scheduled task "([^"]+)"/)?.[1] ?? 'Scheduled task' }
                : {}),
            },
          ]),
        );
        const latest = turns.at(-1)?.sessionId ?? null;
        if (!storedSession() && latest) {
          setSessionId(latest);
          storeSession(latest);
        }
      })
      .catch(() => {});
    void refreshSide();
  }, [refreshSide]);

  // Keep approvals and spend fresh while the app is visible.
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === 'visible') void refreshSide();
    };
    const timer = setInterval(tick, POLL_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [refreshSide]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, thinking]);

  // The voice session calls `ask` long after render; read the conversation id from a ref.
  const sessionRef = useRef(sessionId);
  sessionRef.current = sessionId;

  /** Shows "Remembered: …" under an answer once the background save has happened. */
  const checkRemembered = useCallback((turnId: string) => {
    for (const ms of REMEMBERED_CHECKS_MS) {
      setTimeout(() => {
        void api
          .remembered([turnId])
          .then((found) => {
            const items = found[turnId];
            if (!items?.length) return;
            setMessages((m) =>
              m.map((msg) => (msg.turnId === turnId ? { ...msg, remembered: items } : msg)),
            );
          })
          .catch(() => {});
      }, ms);
    }
  }, []);

  /** Sends one message (typed or spoken) and returns Alfred's reply text, or null on error. */
  const ask = useCallback(
    async (text: string, spoken: boolean): Promise<string | null> => {
      const id = crypto.randomUUID();
      const current = sessionRef.current;
      setMessages((m) => [...m, { id, role: 'you', text, sessionId: current }]);
      setThinking(true);
      try {
        const res = await api.chat(text, current, spoken);
        sessionRef.current = res.sessionId;
        setSessionId(res.sessionId);
        storeSession(res.sessionId);
        setMessages((m) => [
          ...m,
          {
            id: `${id}-r`,
            role: 'alfred',
            turnId: res.turnId,
            text: res.text,
            sessionId: res.sessionId,
            meta: `${spoken ? 'spoken · ' : ''}${res.model.replace(/^claude-/, '')} · $${res.costUsd.toFixed(3)}`,
          },
        ]);
        setStatus((s) => (s ? { ...s, spend: res.spend } : s));
        if (res.approvalIds.length > 0) void refreshSide();
        checkRemembered(res.turnId);
        return res.text;
      } catch (err) {
        setMessages((m) => [
          ...m,
          { id: `${id}-e`, role: 'error', text: describeError(err), sessionId: current },
        ]);
        if (err instanceof ApiFailure && err.code === 'spend-cap') void refreshSide();
        return null;
      } finally {
        setThinking(false);
      }
    },
    [refreshSide, checkRemembered],
  );

  const send = async () => {
    const text = draft.trim();
    if (!text || thinking) return;
    setDraft('');
    await ask(text, false);
    inputRef.current?.focus();
  };

  // --- talking to Alfred ---
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [voiceState, setVoiceState] = useState<VoiceState>('idle');
  const [captions, setCaptions] = useState<{ you: string; alfred: string | null } | null>(null);
  const voice = useRef<VoiceSession | null>(null);
  // Loudness changes every frame: kept out of React state so nothing re-renders for it.
  const level = useRef(0);
  const micRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    void api
      .voiceInfo()
      .then((v) => setVoiceEnabled(v.enabled))
      .catch(() => {});
    return () => voice.current?.stop();
  }, []);

  const toggleVoice = () => {
    voice.current ??= new VoiceSession({
      onState: setVoiceState,
      onLevel: (l) => {
        level.current = l;
        micRef.current?.style.setProperty('--level', String(l));
      },
      ask: async (text) => {
        setCaptions({ you: text, alfred: null });
        const reply = await ask(text, true);
        setCaptions({ you: text, alfred: reply });
        return reply;
      },
      onError: (message) => setNotice(message),
    });
    if (voiceState === 'speaking') return voice.current.interrupt();
    if (voice.current.running) return voice.current.stop();
    setCaptions(null);
    void voice.current.start();
  };
  const endVoice = () => voice.current?.stop();

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void send();
    }
  };

  const newConversation = () => {
    setSessionId(null);
    storeSession(null);
    setMessages((m) =>
      m.length
        ? [
            ...m,
            { id: crypto.randomUUID(), role: 'alfred', text: '', sessionId: null, meta: 'divider' },
          ]
        : m,
    );
    setSettingsOpen(false);
    inputRef.current?.focus();
  };

  const toggleKillSwitch = async () => {
    if (!status) return;
    const engaging = !status.killSwitch.engaged;
    if (!engaging && !window.confirm('Turn Alfred’s tools back on?')) return;
    try {
      const ks = await api.killSwitch({
        engaged: engaging,
        ...(engaging ? { reason: 'stopped from the app' } : {}),
      });
      setStatus({ ...status, killSwitch: ks });
      setNotice(engaging ? 'All tool access stopped.' : 'Tools are back on.');
    } catch (err) {
      setNotice(describeError(err));
    }
  };

  const spend = status?.spend;
  const killed = status?.killSwitch.engaged ?? false;

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <Orb state={thinking ? 'thinking' : 'idle'} size="small" />
          <span>Alfred</span>
        </div>
        <div className="topbar-actions">
          {spend && (
            <div
              className={`spend spend-${spend.state}`}
              title={`Today: $${spend.spentUsd.toFixed(2)} of $${spend.capUsd.toFixed(2)}. Resets at midnight.`}
            >
              <div className="spend-bar">
                <div
                  style={{ width: `${Math.min(100, (spend.spentUsd / spend.capUsd) * 100)}%` }}
                />
              </div>
              <span>${spend.spentUsd.toFixed(2)}</span>
            </div>
          )}
          <button
            className={`btn btn-small ${killed ? 'btn-primary' : 'btn-danger'}`}
            onClick={() => void toggleKillSwitch()}
            title={killed ? 'Turn tools back on' : 'Kill switch: stop all tool access now'}
          >
            {killed ? 'Resume' : 'Stop all'}
          </button>
          <button className="icon-btn" aria-label="Settings" onClick={() => setSettingsOpen(true)}>
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path
                d="M4 7h16M4 12h16M4 17h16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </header>

      <nav className="tabs" aria-label="Sections">
        {VIEWS.map((v) => (
          <button
            key={v}
            className={`tab ${view === v ? 'tab-on' : ''}`}
            aria-current={view === v ? 'page' : undefined}
            onClick={() => go(v)}
          >
            {VIEW_LABELS[v]}
            {v === 'chat' && otherApprovals.length > 0 && (
              <span className="badge" aria-label={`${otherApprovals.length} waiting for approval`}>
                {otherApprovals.length}
              </span>
            )}
            {v === 'posts' && postDrafts.length > 0 && (
              <span className="badge" aria-label={`${postDrafts.length} posts ready`}>
                {postDrafts.length}
              </span>
            )}
          </button>
        ))}
      </nav>

      {killed && (
        <div className="banner banner-bad" role="status">
          Kill switch engaged: Alfred can talk but can’t use any tools.
        </div>
      )}
      {spend?.state === 'capped' && (
        <div className="banner banner-warn" role="status">
          Today’s ${spend.capUsd.toFixed(2)} spending limit is reached. Alfred is paused until
          midnight.
        </div>
      )}
      {notice && (
        <button className="banner banner-info" onClick={() => setNotice(null)}>
          {notice}
        </button>
      )}

      {view === 'posts' && (
        <PostsView drafts={postDrafts} onChanged={refreshSide} onNotice={setNotice} />
      )}
      {view === 'activity' && <ActivityView />}
      {view === 'costs' && <CostsView spend={spend} />}
      {view === 'budget' && <BudgetView />}

      {view === 'chat' && (
        <>
          <main className="conversation" aria-live="polite">
            {messages.length === 0 && !thinking && (
              <div className="empty">
                <HudOr fallback={<Orb state="idle" />}>
                  <Hud state="idle" level={level} size="medium" />
                </HudOr>
                <p>Good evening, Anthony. What can I do for you?</p>
              </div>
            )}
            {messages.map((m) =>
              m.meta === 'divider' ? (
                <div key={m.id} className="divider">
                  New conversation
                </div>
              ) : (
                <div key={m.id} className={`msg msg-${m.role}`}>
                  <div className="bubble">
                    {m.role === 'alfred' ? <Rich text={m.text} /> : m.text}
                  </div>
                  {m.meta && <div className="msg-meta">{m.meta}</div>}
                  {m.remembered?.map((r) => (
                    <div key={r} className="remembered">
                      Remembered: {r}
                    </div>
                  ))}
                  {m.role === 'alfred' && m.turnId && (
                    <Feedback turnId={m.turnId} initial={m.feedback ?? null} />
                  )}
                </div>
              ),
            )}
            {thinking && (
              <div className="msg msg-alfred">
                <div className="bubble typing" aria-label="Alfred is thinking">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}
            <div ref={endRef} />
          </main>

          {postDrafts.length > 0 && (
            <button className="posts-link" onClick={() => go('posts')}>
              {postDrafts.length} {postDrafts.length === 1 ? 'post' : 'posts'} ready → Posts
            </button>
          )}
          <Approvals items={otherApprovals} onChanged={refreshSide} onNotice={setNotice} />

          <form
            className="composer"
            onSubmit={(e) => {
              e.preventDefault();
              void send();
            }}
          >
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKey}
              placeholder={thinking ? 'Alfred is working on it…' : 'Message Alfred'}
              rows={1}
              aria-label="Message Alfred"
              autoFocus
            />
            {voiceEnabled && (
              <button
                ref={micRef}
                type="button"
                className={`mic mic-${voiceState}`}
                onClick={toggleVoice}
                aria-pressed={voiceState !== 'idle'}
                aria-label={VOICE_LABELS[voiceState]}
                title={VOICE_LABELS[voiceState]}
              >
                <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                  {voiceState === 'idle' ? (
                    <path
                      d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3Zm-6 9a6 6 0 0 0 12 0M12 18v3"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  ) : (
                    <rect x="7" y="7" width="10" height="10" rx="2" fill="currentColor" />
                  )}
                </svg>
              </button>
            )}
            <button
              className="btn btn-primary"
              disabled={!draft.trim() || thinking}
              aria-label="Send"
            >
              Send
            </button>
          </form>
        </>
      )}

      {voiceState !== 'idle' && (
        <section className="voice-mode" aria-label="Talking with Alfred">
          <button
            className="voice-mode-orb"
            onClick={toggleVoice}
            aria-label={VOICE_LABELS[voiceState]}
            title={VOICE_LABELS[voiceState]}
          >
            <HudOr fallback={<Orb state={voiceState === 'thinking' ? 'thinking' : 'idle'} />}>
              <Hud state={voiceState} level={level} />
            </HudOr>
          </button>
          <p className="voice-mode-state" role="status">
            {VOICE_LABELS[voiceState].replace(/….*$/, '…')}
          </p>
          <div className="captions" aria-live="polite">
            {captions?.you && <p className="caption-you">{captions.you}</p>}
            {captions?.alfred && <p className="caption-alfred">{captions.alfred}</p>}
          </div>
          <button className="btn voice-mode-end" onClick={endVoice}>
            End conversation
          </button>
        </section>
      )}

      {settingsOpen && status && (
        <Settings
          status={status}
          onClose={() => setSettingsOpen(false)}
          onNewConversation={newConversation}
          onChanged={refreshSide}
          onSignedOut={onSignedOut}
        />
      )}
    </div>
  );
}

/**
 * Shows the HUD, or the plain orb while it loads and on devices where WebGL isn't available.
 */
class HudOr extends Component<{ fallback: ReactNode; children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override render() {
    if (this.state.failed) return this.props.fallback;
    return <Suspense fallback={this.props.fallback}>{this.props.children}</Suspense>;
  }
}
